import { fetchWithAuth } from "../auth/googleAuth";
import {
  deleteFile,
  ensureAppFolder,
  findExistingAppFolder,
  findFileInFolder,
  moveFileToFolder,
  uploadXlsxAsSheet,
} from "./drive";
import { QrSettings, EmailSettings, FormField, Member, Row } from "../types/sheets";
import { isFieldworkerMode } from "../utils/fieldworkerMode";

// In Fieldworker mode the project is shared by the manager account and must never be
// bootstrapped/mutated structurally here.
const FIELDWORKER_NO_CREATE =
  "Fieldworker mode is active: the shared project spreadsheet is missing or incomplete and cannot be created here. Ask the project owner to set it up.";

export const SPREADSHEET_NAME = "mew_dispensers";
const SHEET_TAB = "data";
const CAMPUS_SHEET_TAB = "Campus Values";
const QR_SETTINGS_TAB = "QR Settings";
const FORM_SETTINGS_TAB = "Form Settings";
const SPREADSHEET_ID_KEY = "mew_spreadsheet_id";

const CAMPUS_HEADERS = ["Label", "Value"];
const DEFAULT_CAMPUS_ROWS: string[][] = [
  ["Toyonaka (豊中)", "豊中"],
  ["Suita (吹田)", "吹田"],
  ["Minoh (箕面)", "箕面"],
  ["Other (その他)", "その他"],
];

export const DEFAULT_CAMPUS_OPTIONS = DEFAULT_CAMPUS_ROWS.map(([label, value]) => ({ label, value }));

const QR_SETTINGS_HEADERS = ["Setting", "Value"];
const DEFAULT_QR_MESSAGE = "生理用品の補充・\n不具合等は、こちら\nからお知らせ下さい";

export const DEFAULT_QR_SETTINGS: Readonly<QrSettings> = Object.freeze({
  message: DEFAULT_QR_MESSAGE,
  formUrl: "",
  formId: "",
  formUrlBackup: "",
  formIdBackup: "",
  responsesSheetId: "",
  formLocked: false,
  formLockedBackup: false,
  columns: 3,
  width: 350,
  showLabel: false,
  showInstallLocation: false,
  rows: 8,
  pageSize: "A4",
  orientation: "portrait",
  margin: 10,
  gap: 4,
  watermarkFileId: "",
});

export const QR_PAGE_SIZES: readonly string[] = Object.freeze(["A4", "Letter"]);
export const QR_ORIENTATIONS: readonly string[] = Object.freeze(["portrait", "landscape"]);

function qrSettingsRows(settings: QrSettings): string[][] {
  return [
    ["message", settings.message ?? ""],
    ["formUrl", settings.formUrl ?? ""],
    ["formId", settings.formId ?? ""],
    ["formUrlBackup", settings.formUrlBackup ?? ""],
    ["formIdBackup", settings.formIdBackup ?? ""],
    ["responsesSheetId", settings.responsesSheetId ?? ""],
    ["formLocked", settings.formLocked ? "TRUE" : "FALSE"],
    ["formLockedBackup", settings.formLockedBackup ? "TRUE" : "FALSE"],
    ["columns", String(settings.columns ?? DEFAULT_QR_SETTINGS.columns)],
    ["width", String(settings.width ?? DEFAULT_QR_SETTINGS.width)],
    ["showLabel", settings.showLabel ? "TRUE" : "FALSE"],
    ["showInstallLocation", settings.showInstallLocation ? "TRUE" : "FALSE"],
    ["rows", String(settings.rows ?? DEFAULT_QR_SETTINGS.rows)],
    ["pageSize", settings.pageSize ?? DEFAULT_QR_SETTINGS.pageSize],
    ["orientation", settings.orientation ?? DEFAULT_QR_SETTINGS.orientation],
    ["margin", String(settings.margin ?? DEFAULT_QR_SETTINGS.margin)],
    ["gap", String(settings.gap ?? DEFAULT_QR_SETTINGS.gap)],
    ["watermarkFileId", settings.watermarkFileId ?? ""],
  ];
}

function parseIntOr(value: unknown, fallback: number): number {
  const n = parseInt(String(value ?? "").trim(), 10);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

function parseIntMin0(value: unknown, fallback: number): number {
  const n = parseInt(String(value ?? "").trim(), 10);
  return Number.isFinite(n) && n >= 0 ? n : fallback;
}

function parseEnum(value: unknown, allowed: readonly string[], fallback: string): string {
  const s = String(value ?? "").trim();
  return allowed.includes(s) ? s : fallback;
}

function parseBool(value: unknown, fallback: boolean): boolean {
  const s = String(value ?? "").trim().toLowerCase();
  if (s === "true" || s === "1" || s === "yes") return true;
  if (s === "false" || s === "0" || s === "no") return false;
  return fallback;
}

// "Members" tab: email → tier ("manager" | "fieldworker"). Drives which UI a NON-owner
// sees. A non-owner email not listed (or listed with any non-"manager" value) is treated
// as a fieldworker; the folder owner is always a manager regardless of this tab. This is a
// trusted-team convenience, NOT a security boundary (all tiers are Drive Editors).
const MEMBERS_TAB = "Members";
const MEMBERS_HEADERS = ["Email", "Role"];
export const MEMBER_ROLES = ["manager", "fieldworker"] as const;

const EMAIL_TAB = "Email Settings";
const EMAIL_HEADERS = ["Setting", "Value"];
const EMAIL_FIELDS = ["dest", "cc", "bcc", "sendername", "subject", "topmessage", "bottommessage"] as const;

export const DEFAULT_EMAIL_SETTINGS: Readonly<EmailSettings> = Object.freeze({
  dest: "alefa.network@gmail.com",
  cc: "alefa.network@gmail.com",
  bcc: "",
  sendername: "MeW User Report",
  subject: "MeW ディスペンサー利用者からの連絡",
  topmessage:
    "ダイバーシティー推進課 御中\nMeW プロジェクトのメンバーの皆さま\n\nこのメールは、生理用品無償提供用のディスペンサー利用者が、ディスペンサーについているQRコードを用いて入力したメッセージを自動転送するものです。",
  bottommessage:
    "上記のとおり、連絡が来ていますので補充もしくはご対応をお願いいたします。\nよろしくお願い申し上げます。",
});

function emailSettingsRows(settings: EmailSettings): string[][] {
  return EMAIL_FIELDS.map((k) => [k, settings[k] ?? ""]);
}

const FORM_SETTINGS_HEADERS = ["Label", "Type", "Values", "Description"];
export const FORM_TYPES: readonly string[] = Object.freeze([
  "text", "textarea", "select", "radio", "checkbox", "title",
]);
export const SINGLETON_FORM_TYPES: readonly string[] = Object.freeze([]);
export const QUESTION_FORM_TYPES: readonly string[] = Object.freeze([
  "text", "textarea", "select", "radio", "checkbox",
]);
export const CHOICE_FORM_TYPES: readonly string[] = Object.freeze(["select", "radio", "checkbox"]);
export const ID_LABEL = "ID";

export const DEFAULT_FORM_FIELDS: readonly FormField[] = Object.freeze([
  {
    label: "大阪大学 MeWディスペンサー 連絡フォーム",
    type: "title",
    values: [],
    description:
      "アクセスありがとうございます。\n\n大阪大学内で設置している生理用品無償提供用（MeW）ディスペンサーについて\n\n・生理用品の補充が必要\n・MeWディスペンサーに不具合がある\n\n等があれば、こちらのフォームにご入力ください。\n\nご協力いただき、誠にありがとうございます。\n\n-----------------------------------------------------------------------------------------------------------\nダイバーシティ＆インクルージョンセンター  (https://www.di.osaka-u.ac.jp/dispenser-list/)\n大阪大学ユネスコチェアMeWプロジェクト  (https://mewproject-osaka-u.jp/）",
  },
  { label: "ID", type: "text", values: [], description: "" },
  {
    label: "生理用品の補充が必要な場合",
    type: "checkbox",
    values: [
      "ナプキンがなくなっているので補充が必要",
      "タンポンがなくなっているので補充が必要",
      "その他・・・下記の質問欄にご記入ください",
    ],
    description: "",
  },
  { label: "その他を選択した方は内容をお書きください", type: "text", values: [], description: "" },
  {
    label: "ディスペンサーに不具合がある場合",
    type: "checkbox",
    values: [
      "ディスペンサーが汚れている",
      "ディスペンサーが破損している",
      "ディスペンサーが床に落ちている",
      "ディスペンサーの蓋が開いている",
      "ディスペンサーが濡れている・濡れた形跡がある",
      "ディスペンサーが凹んでいる",
      "ディスペンサーに落書きがされている",
      "その他（下記の質問欄にご記入ください）",
    ],
    description: "",
  },
  {
    label: "具体的にどのような不具合でしたか。（もしあればお書きください。）",
    type: "textarea",
    values: [],
    description: "",
  },
  {
    label:
      "不具合の状態を把握するため、可能であればディスペンサーの写真をmew.info@hus.osaka-u.ac.jpまで送付お願いいたします。ご協力頂ければ幸いです。",
    type: "title",
    values: [],
    description: "",
  },
]);

function formFieldRows(fields: readonly FormField[]): string[][] {
  return fields.map((f) => [
    f.label ?? "",
    f.type ?? "text",
    Array.isArray(f.values) ? f.values.join("|") : String(f.values ?? ""),
    f.description ?? "",
  ]);
}

function normalizeFormField(row: unknown[]): FormField | null {
  const label = String(row?.[0] ?? "").trim();
  const rawType = String(row?.[1] ?? "").trim().toLowerCase();
  const type = FORM_TYPES.includes(rawType) ? rawType : "text";
  if (!label) return null;
  const values = String(row?.[2] ?? "")
    .split("|")
    .map((v) => v.trim())
    .filter((v) => v.length > 0);
  const description = String(row?.[3] ?? "");
  return { label, type, values, description };
}

export function findIdFieldIndex(fields: FormField[]): number {
  const target = ID_LABEL.toLowerCase();
  return fields.findIndex((f) => String(f.label ?? "").trim().toLowerCase() === target);
}

export const HEADERS = [
  "#",
  "ID",
  "担当者",
  "キャンパス",
  "部局、施設名",
  "建物",
  "階",
  "設置場所",
  "備考",
  "アクセス",
  "表記名①",
  "緯度",
  "経度",
  "写真フォルダID",
  "写真ファイルID",
] as const;

export const COL: Record<string, number> = Object.freeze(
  HEADERS.reduce<Record<string, number>>((acc, h, i) => ({ ...acc, [h]: i }), {})
);

// Photos are stored flat in the `pictures` folder and linked to a row solely by their
// Drive file ids, kept as a comma-separated list in the 写真ファイルID column.
export function parsePhotoFileIds(csv: unknown): string[] {
  return String(csv || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

export function mergePhotoFileIds(existing: unknown, newIds: string[]): string {
  return [...parsePhotoFileIds(existing), ...newIds.filter(Boolean)].join(",");
}

export function reportUrl(id: string, formUrl: string): string {
  const base = String(formUrl || "").trim();
  if (!base) return "";
  return `${base}${encodeURIComponent(id)}`;
}

export function computeDisplayName(department: unknown, floor: unknown): string {
  const d = String(department ?? "").trim();
  const f = String(floor ?? "").trim();
  if (!d && !f) return "";
  return `${d} ${f}`.trim();
}

export function displayNameFormula(sheetRowNumber: number): string {
  const dCol = colLetter(COL["部局、施設名"]);
  const fCol = colLetter(COL["階"]);
  return `=${dCol}${sheetRowNumber}&" "&${fCol}${sheetRowNumber}`;
}

function colLetter(idx: number): string {
  let s = "";
  let n = idx;
  do {
    s = String.fromCharCode(65 + (n % 26)) + s;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return s;
}

export const LAST_COL_LETTER = colLetter(HEADERS.length - 1);
export const FULL_RANGE = `${SHEET_TAB}!A1:${LAST_COL_LETTER}`;

export function cellRange(rowIndexZeroBased: number, colIndex: number): string {
  return `${SHEET_TAB}!${colLetter(colIndex)}${rowIndexZeroBased + 2}`;
}

export async function batchUpdateValues(spreadsheetId: string, data: unknown[], valueInputOption = "RAW"): Promise<unknown> {
  if (!data || data.length === 0) return null;
  const res = await fetchWithAuth(
    `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values:batchUpdate`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ valueInputOption, data }),
    }
  );
  if (!res.ok) throw new Error(`Sheets batchUpdate failed (${res.status})`);
  return res.json();
}

/**
 * Check whether a valid project exists, without creating anything.
 * - 'none'  : no cached ID and nothing discoverable in Drive → show chooser
 * - 'stale' : a cached ID was found but Drive says it is gone/trashed → clears the key, show stale modal
 * - 'valid' : a usable spreadsheet exists → normal app flow
 *
 * When there is no usable cached ID we fall back to discovering a project the app itself
 * created in this account's Drive (findExistingProject). Under drive.file the owner's own
 * folder/sheet are listable on any device, so a RETURNING OWNER is auto-reconnected and never
 * sees the chooser — even with empty localStorage (cleared storage, new browser/profile). A
 * fieldworker created no such files, so discovery finds nothing and they fall through to the
 * chooser to re-join. The discovery call caches the id for next time.
 */
export async function checkExistingProject(): Promise<"none" | "stale" | "valid"> {
  const cached = localStorage.getItem(SPREADSHEET_ID_KEY);
  if (cached) {
    const verify = await fetchWithAuth(
      `https://www.googleapis.com/drive/v3/files/${cached}?fields=id,trashed`
    );
    if (verify.ok) {
      const d = (await verify.json()) as { trashed?: boolean };
      if (!d.trashed) return "valid";
    }
    localStorage.removeItem(SPREADSHEET_ID_KEY);
  }

  const discovered = await findExistingProject();
  if (discovered) return "valid";

  // A cached id existed but is gone/trashed and nothing else turned up → stale; otherwise
  // this account simply has no project yet.
  return cached ? "stale" : "none";
}

// Read-only adoption of an existing project: locate the user's MewMaps folder and the
// `mew_dispensers` spreadsheet inside it WITHOUT creating anything, cache the spreadsheet id,
// and return it (or null if no project exists). Backs the onboarding "Continue" button so an
// owner who lost their cached id (new browser/profile, cleared storage) can resume their
// project without the create flow. drive.file only surfaces files this user owns, so a hit
// here is always the user's own project.
export async function findExistingProject(): Promise<string | null> {
  const folderId = await findExistingAppFolder();
  if (!folderId) return null;
  const existing = await findFileInFolder(folderId, SPREADSHEET_NAME);
  if (existing && existing.mimeType === "application/vnd.google-apps.spreadsheet") {
    localStorage.setItem(SPREADSHEET_ID_KEY, existing.id);
    return existing.id;
  }
  return null;
}

// Whether the signed-in user OWNS the given Drive file. Used at startup to decide the UI
// tier: owners are always managers; non-owners are tiered via the Members tab. Defaults to
// false (the safe, fieldworker-leaning answer) if the metadata read fails.
export async function isSpreadsheetOwnedByMe(spreadsheetId: string): Promise<boolean> {
  const res = await fetchWithAuth(
    `https://www.googleapis.com/drive/v3/files/${spreadsheetId}?fields=ownedByMe`
  );
  if (!res.ok) return false;
  const d = (await res.json()) as { ownedByMe?: boolean };
  return !!d.ownedByMe;
}

let spreadsheetInflight: Promise<string> | null = null;
let _lastWasNew = false;

export async function ensureSpreadsheet(): Promise<string> {
  if (spreadsheetInflight) return spreadsheetInflight;
  spreadsheetInflight = (async () => {
    const cached = localStorage.getItem(SPREADSHEET_ID_KEY);
    if (cached) {
      const verify = await fetchWithAuth(
        `https://www.googleapis.com/drive/v3/files/${cached}?fields=id,trashed`
      );
      if (verify.ok) {
        const d = await verify.json() as { trashed?: boolean };
        if (!d.trashed) return cached;
      }
      localStorage.removeItem(SPREADSHEET_ID_KEY);
    }
    return ensureSpreadsheetUncached();
  })();
  try {
    const id = await spreadsheetInflight;
    try {
      await removeDisplayNameProtection(id);
    } catch (e) {
      console.warn("removeDisplayNameProtection failed:", e);
    }
    return id;
  } finally {
    spreadsheetInflight = null;
  }
}

export async function ensureSpreadsheetChecked(): Promise<{ id: string; isNew: boolean }> {
  _lastWasNew = false;
  const id = await ensureSpreadsheet();
  const isNew = _lastWasNew;
  _lastWasNew = false;
  return { id, isNew };
}

async function ensureSpreadsheetUncached(): Promise<string> {
  const folderId = await ensureAppFolder();
  const existing = await findFileInFolder(folderId, SPREADSHEET_NAME);
  if (existing && existing.mimeType === "application/vnd.google-apps.spreadsheet") {
    localStorage.setItem(SPREADSHEET_ID_KEY, existing.id);
    return existing.id;
  }

  if (isFieldworkerMode()) throw new Error(FIELDWORKER_NO_CREATE);

  _lastWasNew = true;
  const createRes = await fetchWithAuth("https://sheets.googleapis.com/v4/spreadsheets", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      properties: { title: SPREADSHEET_NAME },
      sheets: [
        { properties: { title: SHEET_TAB } },
        { properties: { title: CAMPUS_SHEET_TAB } },
        { properties: { title: QR_SETTINGS_TAB } },
        { properties: { title: EMAIL_TAB } },
        { properties: { title: FORM_SETTINGS_TAB } },
        { properties: { title: MEMBERS_TAB } },
      ],
    }),
  });
  if (!createRes.ok) {
    const detail = await createRes.text().catch(() => "");
    throw new Error(`Spreadsheet create failed (${createRes.status})${detail ? `: ${detail}` : ""}`);
  }
  const created = await createRes.json() as { spreadsheetId: string };
  const sheetId = created.spreadsheetId;

  await moveFileToFolder(sheetId, folderId);

  await writeValues(sheetId, `${SHEET_TAB}!A1`, [HEADERS as unknown as string[]]);
  await writeValues(sheetId, `'${CAMPUS_SHEET_TAB}'!A1`, [CAMPUS_HEADERS, ...DEFAULT_CAMPUS_ROWS]);
  await writeValues(sheetId, `'${QR_SETTINGS_TAB}'!A1`, [QR_SETTINGS_HEADERS, ...qrSettingsRows(DEFAULT_QR_SETTINGS)]);
  await writeValues(sheetId, `'${EMAIL_TAB}'!A1`, [EMAIL_HEADERS, ...emailSettingsRows(DEFAULT_EMAIL_SETTINGS)]);
  emailSettingsEnsured.add(sheetId);
  await writeValues(sheetId, `'${FORM_SETTINGS_TAB}'!A1`, [FORM_SETTINGS_HEADERS, ...formFieldRows(DEFAULT_FORM_FIELDS)]);
  // Members starts header-only: the owner is implicitly a manager and seeds the list later.
  await writeValues(sheetId, `'${MEMBERS_TAB}'!A1`, [MEMBERS_HEADERS]);
  membersEnsured.add(sheetId);
  campusSheetEnsured.add(sheetId);
  qrSettingsEnsured.add(sheetId);
  formSettingsEnsured.add(sheetId);
  displayNameProtectionCleaned.add(sheetId);

  localStorage.setItem(SPREADSHEET_ID_KEY, sheetId);
  return sheetId;
}

interface SheetRegistryEntry {
  title: string;
  defaults: unknown[][];
  ensured?: Set<string>;
}

function sheetRegistry(): SheetRegistryEntry[] {
  return [
    { title: SHEET_TAB, defaults: [HEADERS as unknown as string[]] },
    { title: CAMPUS_SHEET_TAB, defaults: [CAMPUS_HEADERS, ...DEFAULT_CAMPUS_ROWS], ensured: campusSheetEnsured },
    { title: QR_SETTINGS_TAB, defaults: [QR_SETTINGS_HEADERS, ...qrSettingsRows(DEFAULT_QR_SETTINGS)], ensured: qrSettingsEnsured },
    { title: EMAIL_TAB, defaults: [EMAIL_HEADERS, ...emailSettingsRows(DEFAULT_EMAIL_SETTINGS)], ensured: emailSettingsEnsured },
    { title: FORM_SETTINGS_TAB, defaults: [FORM_SETTINGS_HEADERS, ...formFieldRows(DEFAULT_FORM_FIELDS)], ensured: formSettingsEnsured },
    { title: MEMBERS_TAB, defaults: [MEMBERS_HEADERS], ensured: membersEnsured },
  ];
}

export async function ensureAllSheets(spreadsheetId: string): Promise<void> {
  // Never mutate the shared spreadsheet's structure when joined as a fieldworker.
  if (isFieldworkerMode()) return;
  const registry = sheetRegistry();
  const res = await fetchWithAuth(
    `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}?fields=sheets(properties(title))`
  );
  if (!res.ok) throw new Error(`Sheets metadata read failed (${res.status})`);
  const data = await res.json() as { sheets?: Array<{ properties?: { title?: string } }> };
  const existing = new Set((data.sheets || []).map((s) => s.properties?.title));

  const missing = registry.filter((r) => !existing.has(r.title));
  if (missing.length > 0) {
    const addRes = await fetchWithAuth(
      `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}:batchUpdate`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          requests: missing.map((r) => ({ addSheet: { properties: { title: r.title } } })),
        }),
      }
    );
    if (!addRes.ok) throw new Error(`Add missing sheets failed (${addRes.status})`);
    for (const r of missing) {
      await writeValues(spreadsheetId, `'${r.title}'!A1`, r.defaults);
    }
  }

  for (const r of registry) {
    if (r.ensured) r.ensured.add(spreadsheetId);
  }
}

const DISPLAY_NAME_PROTECT_DESC = "表記名① is auto-derived from 部局、施設名 and 階";
const displayNameProtectionCleaned = new Set<string>();

async function removeDisplayNameProtection(spreadsheetId: string): Promise<void> {
  if (displayNameProtectionCleaned.has(spreadsheetId)) return;
  const metaRes = await fetchWithAuth(
    `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}?fields=sheets(properties(title),protectedRanges(protectedRangeId,description))`
  );
  if (!metaRes.ok) throw new Error(`Sheets metadata read failed (${metaRes.status})`);
  const meta = await metaRes.json() as {
    sheets?: Array<{
      properties?: { title?: string };
      protectedRanges?: Array<{ protectedRangeId?: number; description?: string }>;
    }>;
  };
  const dataSheet = (meta.sheets || []).find((s) => s.properties?.title === SHEET_TAB);
  if (!dataSheet) {
    displayNameProtectionCleaned.add(spreadsheetId);
    return;
  }
  const toDelete = (dataSheet.protectedRanges || []).filter(
    (p) => p.description === DISPLAY_NAME_PROTECT_DESC
  );
  if (toDelete.length === 0) {
    displayNameProtectionCleaned.add(spreadsheetId);
    return;
  }
  const res = await fetchWithAuth(
    `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}:batchUpdate`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        requests: toDelete.map((p) => ({
          deleteProtectedRange: { protectedRangeId: p.protectedRangeId },
        })),
      }),
    }
  );
  if (!res.ok) throw new Error(`Remove protected range failed (${res.status})`);
  displayNameProtectionCleaned.add(spreadsheetId);
}

const campusSheetEnsured = new Set<string>();

export async function ensureCampusSheet(spreadsheetId: string): Promise<void> {
  if (campusSheetEnsured.has(spreadsheetId)) return;
  const res = await fetchWithAuth(
    `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}?fields=sheets(properties(sheetId,title))`
  );
  if (!res.ok) throw new Error(`Sheets metadata read failed (${res.status})`);
  const data = await res.json() as { sheets?: Array<{ properties?: { title?: string } }> };
  const exists = (data.sheets || []).some((s) => s.properties?.title === CAMPUS_SHEET_TAB);
  if (!exists) {
    const addRes = await fetchWithAuth(
      `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}:batchUpdate`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          requests: [{ addSheet: { properties: { title: CAMPUS_SHEET_TAB } } }],
        }),
      }
    );
    if (!addRes.ok) throw new Error(`Add campus sheet failed (${addRes.status})`);
    await writeValues(spreadsheetId, `'${CAMPUS_SHEET_TAB}'!A1`, [CAMPUS_HEADERS, ...DEFAULT_CAMPUS_ROWS]);
  }
  campusSheetEnsured.add(spreadsheetId);
}

export async function writeCampusOptions(
  spreadsheetId: string,
  rows: { label: string; value: string }[]
): Promise<void> {
  await ensureCampusSheet(spreadsheetId);
  await writeValues(spreadsheetId, `'${CAMPUS_SHEET_TAB}'!A1`, [
    CAMPUS_HEADERS,
    ...rows.map((r) => [r.label, r.value]),
  ]);
}

const qrSettingsEnsured = new Set<string>();

export async function ensureQrSettingsSheet(spreadsheetId: string): Promise<void> {
  if (qrSettingsEnsured.has(spreadsheetId)) return;
  const res = await fetchWithAuth(
    `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}?fields=sheets(properties(sheetId,title))`
  );
  if (!res.ok) throw new Error(`Sheets metadata read failed (${res.status})`);
  const data = await res.json() as { sheets?: Array<{ properties?: { title?: string } }> };
  const exists = (data.sheets || []).some((s) => s.properties?.title === QR_SETTINGS_TAB);
  if (!exists) {
    const addRes = await fetchWithAuth(
      `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}:batchUpdate`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          requests: [{ addSheet: { properties: { title: QR_SETTINGS_TAB } } }],
        }),
      }
    );
    if (!addRes.ok) throw new Error(`Add QR settings sheet failed (${addRes.status})`);
    await writeValues(spreadsheetId, `'${QR_SETTINGS_TAB}'!A1`, [QR_SETTINGS_HEADERS, ...qrSettingsRows(DEFAULT_QR_SETTINGS)]);
  }
  qrSettingsEnsured.add(spreadsheetId);
}

export async function readQrSettings(spreadsheetId: string): Promise<QrSettings> {
  await ensureQrSettingsSheet(spreadsheetId);
  const range = `'${QR_SETTINGS_TAB}'!A2:B`;
  const res = await fetchWithAuth(
    `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${encodeURIComponent(range)}`
  );
  if (!res.ok) return { ...DEFAULT_QR_SETTINGS };
  const data = await res.json() as { values?: unknown[][] };
  const map: Record<string, unknown> = {};
  for (const r of data.values || []) {
    const key = String(r[0] ?? "").trim();
    if (key) map[key] = r[1] ?? "";
  }
  return {
    message: "message" in map ? String(map.message) : DEFAULT_QR_SETTINGS.message,
    formUrl: "formUrl" in map ? String(map.formUrl).trim() : DEFAULT_QR_SETTINGS.formUrl,
    formId: "formId" in map ? String(map.formId).trim() : DEFAULT_QR_SETTINGS.formId,
    formUrlBackup: "formUrlBackup" in map ? String(map.formUrlBackup).trim() : "",
    formIdBackup: "formIdBackup" in map ? String(map.formIdBackup).trim() : "",
    responsesSheetId: "responsesSheetId" in map ? String(map.responsesSheetId).trim() : "",
    formLocked: parseBool(map.formLocked, false),
    formLockedBackup: parseBool(map.formLockedBackup, false),
    columns: parseIntOr(map.columns, DEFAULT_QR_SETTINGS.columns),
    width: parseIntOr(map.width, DEFAULT_QR_SETTINGS.width),
    showLabel: parseBool(map.showLabel, DEFAULT_QR_SETTINGS.showLabel),
    showInstallLocation: parseBool(map.showInstallLocation, DEFAULT_QR_SETTINGS.showInstallLocation),
    rows: parseIntMin0(map.rows, DEFAULT_QR_SETTINGS.rows),
    pageSize: parseEnum(map.pageSize, QR_PAGE_SIZES, DEFAULT_QR_SETTINGS.pageSize),
    orientation: parseEnum(map.orientation, QR_ORIENTATIONS, DEFAULT_QR_SETTINGS.orientation),
    margin: parseIntMin0(map.margin, DEFAULT_QR_SETTINGS.margin),
    gap: parseIntMin0(map.gap, DEFAULT_QR_SETTINGS.gap),
    watermarkFileId: "watermarkFileId" in map ? String(map.watermarkFileId).trim() : "",
    // Read-only marker written by the mewmail backend; never written back by the app
    // (intentionally absent from qrSettingsRows so writeQrSettings leaves it untouched).
    setupComplete: "setupComplete" in map ? String(map.setupComplete).trim() : "",
  };
}

export async function writeQrSettings(spreadsheetId: string, settings: QrSettings): Promise<unknown> {
  await ensureQrSettingsSheet(spreadsheetId);
  const rows = qrSettingsRows(settings);
  // The backend appends "setupComplete" as the last row. Our block now extends onto
  // that position, so re-emit it (with its existing value) to avoid clobbering it.
  // We never originate this marker — only carry through what readQrSettings loaded.
  if (settings.setupComplete) {
    rows.push(["setupComplete", settings.setupComplete]);
  }
  return writeValues(spreadsheetId, `'${QR_SETTINGS_TAB}'!A1`, [
    QR_SETTINGS_HEADERS,
    ...rows,
  ]);
}

// Watermark file id is carried in QR Settings so fieldworkers can resolve it from the
// sheet (they can't list the shared assets folder under drive.file).
export async function readWatermarkFileId(spreadsheetId: string): Promise<string> {
  const s = await readQrSettings(spreadsheetId);
  return s.watermarkFileId || "";
}

export async function writeWatermarkFileId(spreadsheetId: string, watermarkFileId: string): Promise<void> {
  const s = await readQrSettings(spreadsheetId);
  if ((s.watermarkFileId || "") === (watermarkFileId || "")) return;
  await writeQrSettings(spreadsheetId, { ...s, watermarkFileId });
}

const emailSettingsEnsured = new Set<string>();

export async function ensureEmailSheet(spreadsheetId: string): Promise<void> {
  if (emailSettingsEnsured.has(spreadsheetId)) return;
  const res = await fetchWithAuth(
    `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}?fields=sheets(properties(sheetId,title))`
  );
  if (!res.ok) throw new Error(`Sheets metadata read failed (${res.status})`);
  const data = await res.json() as { sheets?: Array<{ properties?: { title?: string } }> };
  const exists = (data.sheets || []).some((s) => s.properties?.title === EMAIL_TAB);
  if (!exists) {
    const addRes = await fetchWithAuth(
      `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}:batchUpdate`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          requests: [{ addSheet: { properties: { title: EMAIL_TAB } } }],
        }),
      }
    );
    if (!addRes.ok) throw new Error(`Add email sheet failed (${addRes.status})`);
    await writeValues(spreadsheetId, `'${EMAIL_TAB}'!A1`, [EMAIL_HEADERS, ...emailSettingsRows(DEFAULT_EMAIL_SETTINGS)]);
  }
  emailSettingsEnsured.add(spreadsheetId);
}

export async function readEmailSettings(spreadsheetId: string): Promise<EmailSettings> {
  await ensureEmailSheet(spreadsheetId);
  const range = `'${EMAIL_TAB}'!A2:B`;
  const res = await fetchWithAuth(
    `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${encodeURIComponent(range)}`
  );
  if (!res.ok) return { ...DEFAULT_EMAIL_SETTINGS };
  const data = await res.json() as { values?: unknown[][] };
  const map: Record<string, unknown> = {};
  for (const r of data.values || []) {
    const key = String(r[0] ?? "").trim();
    if (key) map[key] = r[1];
  }
  const out = {} as EmailSettings;
  for (const k of EMAIL_FIELDS) {
    out[k] = map[k] != null ? String(map[k]) : DEFAULT_EMAIL_SETTINGS[k];
  }
  return out;
}

export async function writeEmailSettings(spreadsheetId: string, settings: EmailSettings): Promise<unknown> {
  await ensureEmailSheet(spreadsheetId);
  return writeValues(spreadsheetId, `'${EMAIL_TAB}'!A1`, [
    EMAIL_HEADERS,
    ...emailSettingsRows(settings),
  ]);
}

const membersEnsured = new Set<string>();

// Structural (creates the Members tab if missing) — therefore MANAGER-ONLY. Do NOT call
// from the read path used for tier resolution: a fieldworker has no right to add tabs.
export async function ensureMembersSheet(spreadsheetId: string): Promise<void> {
  if (membersEnsured.has(spreadsheetId)) return;
  const res = await fetchWithAuth(
    `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}?fields=sheets(properties(sheetId,title))`
  );
  if (!res.ok) throw new Error(`Sheets metadata read failed (${res.status})`);
  const data = await res.json() as { sheets?: Array<{ properties?: { title?: string } }> };
  const exists = (data.sheets || []).some((s) => s.properties?.title === MEMBERS_TAB);
  if (!exists) {
    const addRes = await fetchWithAuth(
      `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}:batchUpdate`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          requests: [{ addSheet: { properties: { title: MEMBERS_TAB } } }],
        }),
      }
    );
    if (!addRes.ok) throw new Error(`Add members sheet failed (${addRes.status})`);
    await writeValues(spreadsheetId, `'${MEMBERS_TAB}'!A1`, [MEMBERS_HEADERS]);
  }
  membersEnsured.add(spreadsheetId);
}

// Reads the Members tab WITHOUT creating it, so fieldworkers (who can't add tabs) can read
// their own tier. A missing tab / read failure / empty list all resolve to [] (→ everyone
// non-owner is a fieldworker, the safe default). Any role other than exactly "manager" is
// coerced to "fieldworker".
export async function readMembers(spreadsheetId: string): Promise<Member[]> {
  const range = `'${MEMBERS_TAB}'!A2:B`;
  const res = await fetchWithAuth(
    `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${encodeURIComponent(range)}`
  );
  if (!res.ok) return [];
  const data = await res.json() as { values?: unknown[][] };
  const out: Member[] = [];
  for (const r of data.values || []) {
    const email = String(r[0] ?? "").trim().toLowerCase();
    if (!email) continue;
    const role = String(r[1] ?? "").trim().toLowerCase() === "manager" ? "manager" : "fieldworker";
    out.push({ email, role });
  }
  return out;
}

// Manager-only. Normalizes emails (lowercased, blanks dropped, de-duplicated keeping the
// last role given) and wipe-rewrites the tab so removed rows actually disappear.
export async function writeMembers(spreadsheetId: string, members: Member[]): Promise<unknown> {
  await ensureMembersSheet(spreadsheetId);
  const byEmail = new Map<string, Member["role"]>();
  for (const m of members) {
    const email = (m.email || "").trim().toLowerCase();
    if (!email) continue;
    byEmail.set(email, m.role === "manager" ? "manager" : "fieldworker");
  }
  const rows = Array.from(byEmail, ([email, role]) => [email, role]);
  const sheetId = await getSheetIdByTitle(spreadsheetId, MEMBERS_TAB);
  await fetchWithAuth(
    `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}:batchUpdate`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        requests: [{ updateCells: { range: { sheetId }, fields: "userEnteredValue" } }],
      }),
    }
  );
  return writeValues(spreadsheetId, `'${MEMBERS_TAB}'!A1`, [MEMBERS_HEADERS, ...rows]);
}

// Resolves a NON-owner's tier from the Members tab. Empty email or no matching manager row
// → "fieldworker" (the safe default). Owner-override is handled by the caller, not here.
export async function resolveTierFromMembers(
  spreadsheetId: string,
  email: string | null
): Promise<"manager" | "fieldworker"> {
  if (!email) return "fieldworker";
  const target = email.trim().toLowerCase();
  if (!target) return "fieldworker";
  const members = await readMembers(spreadsheetId);
  const match = members.find((m) => m.email === target);
  return match && match.role === "manager" ? "manager" : "fieldworker";
}

const formSettingsEnsured = new Set<string>();

export async function ensureFormSettingsSheet(spreadsheetId: string): Promise<void> {
  if (formSettingsEnsured.has(spreadsheetId)) return;
  const res = await fetchWithAuth(
    `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}?fields=sheets(properties(sheetId,title))`
  );
  if (!res.ok) throw new Error(`Sheets metadata read failed (${res.status})`);
  const data = await res.json() as { sheets?: Array<{ properties?: { title?: string } }> };
  const exists = (data.sheets || []).some((s) => s.properties?.title === FORM_SETTINGS_TAB);
  if (!exists) {
    const addRes = await fetchWithAuth(
      `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}:batchUpdate`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          requests: [{ addSheet: { properties: { title: FORM_SETTINGS_TAB } } }],
        }),
      }
    );
    if (!addRes.ok) throw new Error(`Add form settings sheet failed (${addRes.status})`);
    await writeValues(
      spreadsheetId,
      `'${FORM_SETTINGS_TAB}'!A1`,
      [FORM_SETTINGS_HEADERS, ...formFieldRows(DEFAULT_FORM_FIELDS)]
    );
  }
  formSettingsEnsured.add(spreadsheetId);
}

export async function readFormFields(spreadsheetId: string): Promise<FormField[]> {
  await ensureFormSettingsSheet(spreadsheetId);
  const range = `'${FORM_SETTINGS_TAB}'!A2:D`;
  const res = await fetchWithAuth(
    `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${encodeURIComponent(range)}`
  );
  if (!res.ok) return DEFAULT_FORM_FIELDS.map((f) => ({ ...f, values: [...f.values] }));
  const data = await res.json() as { values?: unknown[][] };
  const rows = data.values || [];
  const fields: FormField[] = [];
  for (const r of rows) {
    const f = normalizeFormField(r as unknown[]);
    if (f) fields.push(f);
  }
  if (fields.length === 0) {
    return DEFAULT_FORM_FIELDS.map((f) => ({ ...f, values: [...f.values] }));
  }
  return fields;
}

export async function writeFormFields(spreadsheetId: string, fields: FormField[]): Promise<unknown> {
  await ensureFormSettingsSheet(spreadsheetId);
  const sheetId = await getSheetIdByTitle(spreadsheetId, FORM_SETTINGS_TAB);
  await fetchWithAuth(
    `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}:batchUpdate`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        requests: [
          {
            updateCells: {
              range: { sheetId },
              fields: "userEnteredValue",
            },
          },
        ],
      }),
    }
  );
  return writeValues(
    spreadsheetId,
    `'${FORM_SETTINGS_TAB}'!A1`,
    [FORM_SETTINGS_HEADERS, ...formFieldRows(fields)]
  );
}

async function getSheetIdByTitle(spreadsheetId: string, title: string): Promise<number> {
  const res = await fetchWithAuth(
    `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}?fields=sheets(properties(sheetId,title))`
  );
  if (!res.ok) throw new Error(`Sheets metadata read failed (${res.status})`);
  const data = await res.json() as { sheets?: Array<{ properties?: { sheetId?: number; title?: string } }> };
  const sheet = (data.sheets || []).find((s) => s.properties?.title === title);
  if (!sheet || sheet.properties?.sheetId == null) throw new Error(`Sheet tab '${title}' not found`);
  return sheet.properties.sheetId;
}

export async function readCampusOptions(spreadsheetId: string): Promise<Array<{ label: string; value: string }> | null> {
  await ensureCampusSheet(spreadsheetId);
  const range = `'${CAMPUS_SHEET_TAB}'!A2:B`;
  const res = await fetchWithAuth(
    `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${encodeURIComponent(range)}`
  );
  if (!res.ok) return null;
  const data = await res.json() as { values?: unknown[][] };
  const values = data.values || [];
  const opts = values
    .map((r) => ({ label: String(r[0] ?? "").trim(), value: String(r[1] ?? "").trim() }))
    .filter((o) => o.value);
  return opts.length > 0 ? opts : null;
}

export async function readAllRows(spreadsheetId: string): Promise<Row[]> {
  const res = await fetchWithAuth(
    `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${encodeURIComponent(FULL_RANGE)}`
  );
  if (!res.ok) throw new Error(`Sheets read failed (${res.status})`);
  const data = await res.json() as { values?: unknown[][] };
  const values = data.values || [];
  const [, ...rows] = values;
  return rows.map((r) => {
    const padded = [...(r as Row)];
    while (padded.length < HEADERS.length) padded.push("");
    return padded;
  });
}

export async function writeValues(spreadsheetId: string, range: string, values: unknown[][]): Promise<unknown> {
  const res = await fetchWithAuth(
    `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${encodeURIComponent(range)}?valueInputOption=USER_ENTERED`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ values }),
    }
  );
  if (!res.ok) throw new Error(`Sheets write failed (${res.status})`);
  return res.json();
}

export async function appendRows(spreadsheetId: string, rows: Row[]): Promise<unknown> {
  const res = await fetchWithAuth(
    `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${encodeURIComponent(FULL_RANGE)}:append?valueInputOption=USER_ENTERED&insertDataOption=INSERT_ROWS`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ values: rows }),
    }
  );
  if (!res.ok) throw new Error(`Sheets append failed (${res.status})`);
  return res.json();
}

export async function appendRowsWide(spreadsheetId: string, rows: unknown[][]): Promise<unknown> {
  const range = `${SHEET_TAB}!A1`;
  const res = await fetchWithAuth(
    `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${encodeURIComponent(range)}:append?valueInputOption=USER_ENTERED&insertDataOption=INSERT_ROWS`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ values: rows }),
    }
  );
  if (!res.ok) throw new Error(`Sheets append failed (${res.status})`);
  return res.json();
}

export async function extendHeaderRow(spreadsheetId: string, extraHeaders: string[]): Promise<unknown> {
  const fullHeader = [...HEADERS, ...extraHeaders];
  const endCol = colLetter(fullHeader.length - 1);
  const range = `${SHEET_TAB}!A1:${endCol}1`;
  return writeValues(spreadsheetId, range, [fullHeader]);
}

export async function updateCell(spreadsheetId: string, rowIndexZeroBased: number, colIndex: number, value: unknown): Promise<unknown> {
  const sheetRowNumber = rowIndexZeroBased + 2;
  const range = `${SHEET_TAB}!${colLetter(colIndex)}${sheetRowNumber}`;
  return writeValues(spreadsheetId, range, [[value]]);
}

async function getDataSheetId(spreadsheetId: string): Promise<number> {
  const res = await fetchWithAuth(
    `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}?fields=sheets(properties(sheetId,title))`
  );
  if (!res.ok) throw new Error(`Sheets metadata read failed (${res.status})`);
  const data = await res.json() as { sheets?: Array<{ properties?: { sheetId?: number; title?: string } }> };
  const sheet = (data.sheets || []).find((s) => s.properties?.title === SHEET_TAB);
  if (!sheet || sheet.properties?.sheetId == null) throw new Error(`Sheet tab '${SHEET_TAB}' not found`);
  return sheet.properties.sheetId;
}

export async function deleteRow(spreadsheetId: string, rowIndexZeroBased: number): Promise<unknown> {
  const sheetId = await getDataSheetId(spreadsheetId);
  const startIndex = rowIndexZeroBased + 1;
  const res = await fetchWithAuth(
    `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}:batchUpdate`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        requests: [
          {
            deleteDimension: {
              range: {
                sheetId,
                dimension: "ROWS",
                startIndex,
                endIndex: startIndex + 1,
              },
            },
          },
        ],
      }),
    }
  );
  if (!res.ok) throw new Error(`Sheets delete row failed (${res.status})`);
  return res.json();
}

export async function updateRow(spreadsheetId: string, rowIndexZeroBased: number, rowValues: Row): Promise<unknown> {
  const sheetRowNumber = rowIndexZeroBased + 2;
  const range = `${SHEET_TAB}!A${sheetRowNumber}:${LAST_COL_LETTER}${sheetRowNumber}`;
  return writeValues(spreadsheetId, range, [rowValues]);
}

async function deleteOtherSheetsByName(folderId: string, name: string, keepId: string): Promise<void> {
  const q = `name='${name.replace(/'/g, "\\'")}' and '${folderId}' in parents and mimeType='application/vnd.google-apps.spreadsheet' and trashed=false`;
  const res = await fetchWithAuth(
    `https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(q)}&spaces=drive&pageSize=50&fields=files(id,name)`
  );
  if (!res.ok) return;
  const data = await res.json() as { files?: Array<{ id: string }> };
  for (const f of data.files || []) {
    if (f.id === keepId) continue;
    try {
      await deleteFile(f.id);
    } catch (e) {
      console.warn(`Failed to delete old sheet ${f.id}:`, e);
    }
  }
}

async function getFirstSheetProperties(spreadsheetId: string): Promise<{ sheetId: number; title: string; index: number }> {
  const res = await fetchWithAuth(
    `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}?fields=sheets(properties(sheetId,title,index))`
  );
  if (!res.ok) throw new Error(`Sheets metadata read failed (${res.status})`);
  const data = await res.json() as { sheets?: Array<{ properties?: { sheetId?: number; title?: string; index?: number } }> };
  const sheets = data.sheets || [];
  if (sheets.length === 0) throw new Error("No sheets in spreadsheet");
  sheets.sort((a, b) => (a.properties?.index ?? 0) - (b.properties?.index ?? 0));
  const p = sheets[0].properties!;
  return { sheetId: p.sheetId!, title: p.title!, index: p.index! };
}

async function renameSheetTab(spreadsheetId: string, sheetId: number, newTitle: string): Promise<unknown> {
  const res = await fetchWithAuth(
    `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}:batchUpdate`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        requests: [
          {
            updateSheetProperties: {
              properties: { sheetId, title: newTitle },
              fields: "title",
            },
          },
        ],
      }),
    }
  );
  if (!res.ok) throw new Error(`Sheet rename failed (${res.status})`);
  return res.json();
}

async function readFirstRow(spreadsheetId: string, sheetTitle: string, width: number): Promise<unknown[]> {
  const endCol = colLetter(width - 1);
  const range = `${sheetTitle}!A1:${endCol}1`;
  const res = await fetchWithAuth(
    `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${encodeURIComponent(range)}`
  );
  if (!res.ok) throw new Error(`Sheets header read failed (${res.status})`);
  const data = await res.json() as { values?: unknown[][] };
  return (data.values && data.values[0]) || [];
}

export async function replaceSpreadsheetFromXlsx(file: File): Promise<string> {
  const folderId = await ensureAppFolder();

  const newSheet = await uploadXlsxAsSheet(folderId, SPREADSHEET_NAME, file);
  const newId = newSheet.id;

  try {
    const firstProps = await getFirstSheetProperties(newId);

    const probeTitle = firstProps.title;
    const firstRow = await readFirstRow(newId, probeTitle, HEADERS.length);
    const ok =
      firstRow.length >= HEADERS.length &&
      HEADERS.every((h, i) => String(firstRow[i] ?? "").trim() === h);
    if (!ok) {
      await deleteFile(newId);
      const err = Object.assign(new Error("OVERWRITE_COLUMN_ORDER"), { code: "OVERWRITE_COLUMN_ORDER" });
      throw err;
    }

    if (firstProps.title !== SHEET_TAB) {
      await renameSheetTab(newId, firstProps.sheetId, SHEET_TAB);
    }

    await deleteOtherSheetsByName(folderId, SPREADSHEET_NAME, newId);

    try {
      await ensureCampusSheet(newId);
    } catch (e) {
      console.warn("ensureCampusSheet after import failed:", e);
    }

    displayNameProtectionCleaned.add(newId);

    localStorage.setItem(SPREADSHEET_ID_KEY, newId);
    return newId;
  } catch (e) {
    if ((e as { code?: string }).code !== "OVERWRITE_COLUMN_ORDER") {
      try {
        await deleteFile(newId);
      } catch {}
    }
    throw e;
  }
}

export function spreadsheetUrl(id: string): string {
  return `https://docs.google.com/spreadsheets/d/${id}/edit`;
}

export function findRowIndexById(rows: Row[], id: string): number {
  return rows.findIndex((r) => (r[COL["ID"]] || "") === id);
}

// All localStorage keys that reference Drive/Sheets resources for a specific project.
// Keys not listed here (auth token, language, UI prefs) are intentionally preserved.
const MEW_PROJECT_STORAGE_KEYS = [
  SPREADSHEET_ID_KEY,           // "mew_spreadsheet_id"
  "mew_drive_root_id",
  "mew_pictures_folder_id",
  "mew_maps_folder_id",
  "mew_assets_folder_id",
  "mew_forms_folder_id",
  "mew_icon_file_id",
  "mew_common_picture_file_id",
  "mew_watermark_file_id",
  "mew_public_folders",
  "mew_photo_url_manifest",
  "mew.fieldworker",            // joined-project mode flag — only meaningful with the above
  "mew.shared_project",         // joined-project marker (set for any non-owner project)
] as const;

/** Returns true if the browser has any project-resource localStorage keys set. */
export function hasMewProjectData(): boolean {
  return MEW_PROJECT_STORAGE_KEYS.some((k) => localStorage.getItem(k) !== null);
}

/** Clears all project-resource localStorage keys. Preserves auth token, language, and UI preferences. */
export function clearMewProjectData(): void {
  for (const k of MEW_PROJECT_STORAGE_KEYS) localStorage.removeItem(k);
}
